import Dinero, { Currency } from 'dinero.js';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

// GraphQL
import {
    DeleteHoldDocument,
    OrderSummaryEventQuery,
    PaymentPageEventQuery,
    TicketCategoryObject,
} from '../../graphql/graphql';

// MUI
import EditIcon from '@mui/icons-material/Edit';
import {
    Box,
    Checkbox,
    checkboxClasses,
    FormControl,
    FormHelperText,
    Input,
    Modal,
    Stack,
    Typography,
} from '@mui/joy';

// Components
import { CheckoutBtn } from './checkoutBtnJoy.component.tsx';

// Styled
import {
    StyledActionButton,
    StyledBorderButton,
    StyledButton,
} from '../common/styled/ActionablesJoy.styled.tsx';
import { StyledTextPrimary } from '../common/styled/StaticElementsJoy.styled.tsx';
import { StyledModalBox } from '../payment/paymentPageJoy.styled.tsx';
import { CheckoutStack, MoneyBox } from './orderSummaryPageJoy.styled.tsx';

// Images
import ProLogo from '../../assets/admit-one-pro.svg';
import Logo from '../../assets/poweredByLogo.png';

// Functions
import { COMMUNITY_URL } from '@/config/index.ts';
import { useApolloClient } from '@apollo/client';
import { calculateSubtotal, FeeItem } from '../../utils/calculations.ts';
import { SafeChargeContext } from '../common/safecharge.provider';
import { CartContext } from '../providers/cart.tsx';
import PromoCodeInput from './PromoCodeInput.tsx';

export interface CheckoutCardProps {
    event: OrderSummaryEventQuery['event'] | PaymentPageEventQuery['event'];
    organizerPrimaryColor: string;
    organizerSecondaryColor?: string;
    disablePurchase: boolean;
    paymentPage?: boolean;
    soldOut?: boolean;
    currency: string;
    setErrorAlert?: (err: string | null) => void;
    hasAdditionalTerms: boolean;
    onContinueClick?: () => void;
    hasQuestionnaire?: boolean;
}

export interface MatchedCategories extends TicketCategoryObject {
    quantitySelected: number;
}

export default function CheckoutCard({
    event,
    organizerPrimaryColor,
    disablePurchase = false,
    paymentPage = false,
    soldOut = false,
    hasAdditionalTerms,
    onContinueClick,
    hasQuestionnaire,
}: CheckoutCardProps) {
    const { t } = useTranslation();
    const history = useHistory();

    const [editCheckout, setEditCheckout] = useState(false);
    const [agreedToToS, setAgreedtoToS] = useState(false);
    const [specialRequests, setSpecialRequests] = useState<string>('');
    const apollo = useApolloClient();

    const { cart, getCalculations, setCart } = useContext(CartContext);

    const [promoCode, setPromoCode] = useState<string[]>(() => cart.promoCodes || []);

    useEffect(() => {
        setCart({ promoCodes: promoCode });
    }, [promoCode, setCart]);

    const { lineItems, feeItems, total } = useMemo(
        () => getCalculations(event),
        [getCalculations, event]
    );

    useEffect(() => {
        if (agreedToToS && lineItems.length === 0) {
            setAgreedtoToS(false);
        }
    }, [lineItems, agreedToToS]);

    const goBack = async () => {
        try {
            if (cart.hold?.id) {
                await apollo.mutate({
                    mutation: DeleteHoldDocument,
                    variables: {
                        deleteHoldId: cart.hold.id,
                        addToInventory: true,
                    },
                });
            }

            history.goBack();
        } catch (err) {
            console.error(err);
            history.goBack();
        }
    };

    const safeCharge = useContext(SafeChargeContext);

    const isEnabled = Boolean(
        lineItems.length > 0 && !disablePurchase && !soldOut && safeCharge && agreedToToS
    );

    const checkoutNotesField = event?.checkoutCustomFields?.[0];

    // Helper function to translate common fee names
    const getTranslatedFeeName = (feeName: string): string => {
        const feeNameLower = feeName.toLowerCase();
        if (feeNameLower.includes('service fee') || feeNameLower === 'service fee') {
            return t('checkoutCard.fees.serviceFee', 'Service Fee');
        }
        if (feeNameLower.includes('processing fee') || feeNameLower === 'processing fee') {
            return t('checkoutCard.fees.processingFee', 'Processing Fee');
        }
        // Return original name if no translation found
        return feeName;
    };

    const groupFeesByGroup = (
        feeItems: FeeItem[],
        event: OrderSummaryEventQuery['event'] | PaymentPageEventQuery['event']
    ) => {
        if (!event?.feesGroups || event.feesGroups.length === 0) {
            return feeItems;
        }

        const groupedFees: { [key: string]: any } = {};
        const ungroupedFees: FeeItem[] = [];

        feeItems.forEach((fee) => {
            const feeGroup = event?.feesGroups?.find((group: any) =>
                group.feeIds?.includes(fee.id)
            );

            if (feeGroup) {
                const groupKey = feeGroup.name;

                if (!groupedFees[groupKey]) {
                    groupedFees[groupKey] = {
                        ...feeGroup,
                        subTotal: 0,
                        order: Math.min(
                            ...feeItems
                                .filter((f) => feeGroup?.feeIds?.includes(f.id))
                                .map((f) => f.order ?? 0)
                        ),
                    };
                }
                groupedFees[groupKey].subTotal += fee.subTotal;
            } else {
                ungroupedFees.push(fee);
            }
        });

        return [...Object.values(groupedFees), ...ungroupedFees];
    };

    return (
        <>
            <Modal open={editCheckout} onClose={() => setEditCheckout(false)}>
                <StyledModalBox>
                    <Stack>
                        <Typography sx={{ fontWeight: 500, fontSize: 24, marginBottom: 3 }}>
                            {t('checkoutCard.modal.leaveCheckout', 'Leave checkout to edit order?')}
                        </Typography>
                        <Box
                            sx={{
                                alignSelf: 'flex-end',
                                display: 'flex',
                                alignItems: 'flex-start',
                            }}
                        >
                            <StyledBorderButton
                                sx={{ marginTop: '0 !important', marginRight: '10px' }}
                                onClick={() => setEditCheckout(false)}
                                backgroundcolor={organizerPrimaryColor}
                            >
                                {t('checkoutCard.modal.stayInCheckout', 'No, stay in Checkout')}
                            </StyledBorderButton>
                            <StyledButton backgroundcolor={organizerPrimaryColor} onClick={goBack}>
                                {t('checkoutCard.modal.editOrder', "Yes, I'd like to edit my order")}
                            </StyledButton>
                        </Box>
                    </Stack>
                </StyledModalBox>
            </Modal>
            <Stack sx={{ position: { lg: 'sticky', sm: 'relative' }, top: 15 }}>
                <CheckoutStack
                    sx={{ backgroundColor: (theme) => theme.palette.neutral[50], marginBottom: 2 }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography level={'body-md'} sx={{ fontWeight: 600 }}>
                            {t('checkoutCard.title', 'Checkout')}
                        </Typography>
                        {paymentPage && (
                            <StyledActionButton
                                id={'edit_order_button'}
                                variant="plain"
                                onClick={() => setEditCheckout(true)}
                                setcolor={organizerPrimaryColor}
                                startDecorator={<EditIcon />}
                            >
                                {t('checkoutCard.editOrder', 'Edit Order')}
                            </StyledActionButton>
                        )}
                    </Box>
                    <>
                        <Stack sx={{ my: 3 }}>
                            {lineItems?.length > 0 ? (
                                lineItems.map((item) => {
                                    const hasDiscount = item.discount && item.discount > 0;
                                    return (
                                        <Box key={item.id} sx={{ mb: 1 }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                }}
                                            >
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 16,
                                                            fontWeight: 700,
                                                            lineHeight: '150%',
                                                        }}
                                                    >
                                                        {item.quantity} x {item.name}
                                                    </Typography>
                                                    {hasDiscount && (
                                                        <Box>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 12,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                    ml: 1.5,
                                                                }}
                                                            >
                                                                <span>
                                                                    {t('checkoutCard.promoCode', 'Promo Code:')} {item.promoCode}
                                                                </span>
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 16,
                                                            lineHeight: '150%',
                                                        }}
                                                    >
                                                        {Dinero({
                                                            amount: Number(
                                                                item?.originalSubTotal ||
                                                                    item?.subTotal ||
                                                                    0
                                                            ),
                                                            currency: event!.currency as Currency,
                                                        })
                                                            .setLocale(
                                                                event!.currency === 'USD'
                                                                    ? 'en-US'
                                                                    : 'en-CA'
                                                            )
                                                            .toFormat('$0,0.00')}
                                                    </Typography>
                                                    {hasDiscount && (
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                lineHeight: '150%',
                                                            }}
                                                        >
                                                            -
                                                            {Dinero({
                                                                amount: Number(item?.discount || 0),
                                                                currency: event!
                                                                    .currency as Currency,
                                                            })
                                                                .setLocale(
                                                                    event!.currency === 'USD'
                                                                        ? 'en-US'
                                                                        : 'en-CA'
                                                                )
                                                                .toFormat('$0,0.00')}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })
                            ) : (
                                <Typography sx={{ my: 3, fontSize: 16 }}>--</Typography>
                            )}
                        </Stack>

                        <MoneyBox>
                            <Typography>{t('checkoutCard.subtotal', 'Subtotal:')}</Typography>
                            <Typography>
                                {Dinero({
                                    amount: calculateSubtotal(lineItems),
                                    currency: event!.currency as Currency,
                                })
                                    .setLocale(event!.currency === 'USD' ? 'en-US' : 'en-CA')
                                    .toFormat('$0,0.00')}
                            </Typography>
                        </MoneyBox>

                        {groupFeesByGroup(feeItems, event)
                            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                            .map((fee) => (
                                <MoneyBox key={fee.id}>
                                    <Typography>{getTranslatedFeeName(fee.name)}:</Typography>
                                    <Typography>
                                        {Dinero({
                                            amount: fee.subTotal,
                                            currency: event!.currency as Currency,
                                        })
                                            .setLocale(
                                                event!.currency === 'USD' ? 'en-US' : 'en-CA'
                                            )
                                            .toFormat('$0,0.00')}
                                    </Typography>
                                </MoneyBox>
                            ))}

                        <MoneyBox>
                            <Typography sx={{ fontWeight: '700' }}>
                                {t('checkoutCard.total', 'Total ({{currency}}):', { currency: event!.currency })}
                            </Typography>
                            <Typography sx={{ fontWeight: '700' }}>
                                {Dinero({
                                    amount: total,
                                    currency: event!.currency as Currency,
                                })
                                    .setLocale(event!.currency === 'USD' ? 'en-US' : 'en-CA')
                                    .toFormat('$0,0.00')}
                            </Typography>
                        </MoneyBox>

                        <Stack sx={{ mt: 3 }}>
                            <Typography sx={{ lineHeight: '143%', fontWeight: '700' }}>
                                {t('checkoutCard.allAmountsIn', 'All amounts are in')}{' '}
                                <span style={{ fontWeight: 600 }}>
                                    {event!.currency === 'USD' 
                                        ? t('checkoutCard.usDollars', 'US Dollars')
                                        : t('checkoutCard.canadianDollars', 'Canadian Dollars')}
                                </span>
                                .
                            </Typography>
                            <Typography sx={{ lineHeight: '143%' }}>
                                {t('checkoutCard.feesNotRefundable', 'Applicable fees are not refundable.')}
                            </Typography>
                        </Stack>
                    </>

                    {!paymentPage && (
                        <>
                            {event?.checkoutPromoCodes && event?.checkoutPromoCodes?.length > 0 && (
                                <Stack sx={{ mt: 3, mb: 3 }}>
                                    <Typography level="body-xs" sx={{ fontWeight: 600 }}>
                                        {t('checkoutCard.havePromoCode', 'Have a Promo Code?')}
                                    </Typography>
                                    <PromoCodeInput
                                        promoCodes={promoCode}
                                        setPromoCodes={setPromoCode}
                                        eventPromoCodes={event?.checkoutPromoCodes}
                                        organizerPrimaryColor={organizerPrimaryColor}
                                    />
                                </Stack>
                            )}

                            {checkoutNotesField?.id && (
                                <Stack sx={{ mt: 3 }}>
                                    <StyledTextPrimary
                                        level="body-sm"
                                        sx={{ fontWeight: 600, mb: 0.5 }}
                                    >
                                        {checkoutNotesField?.label || t('checkoutCard.specialRequests', 'Special Requests (Optional)')}
                                    </StyledTextPrimary>
                                    <FormControl
                                        id="special-requests-control"
                                        size="md"
                                        color="neutral"
                                        error={false}
                                        sx={{ width: '100%' }}
                                    >
                                        <Input
                                            color="neutral"
                                            id="special-requests"
                                            name="special-requests"
                                            disabled={lineItems.length === 0}
                                            onChange={(e) => {
                                                if (
                                                    e.target.value.length <=
                                                    (checkoutNotesField?.maxLimit || 100)
                                                ) {
                                                    setSpecialRequests(e.target.value);
                                                }
                                            }}
                                            fullWidth
                                            value={specialRequests}
                                            variant="outlined"
                                            sx={{
                                                '&:focus-within::before': {
                                                    boxShadow: `inset 0px 0px 1px 1px ${organizerPrimaryColor}!important`,
                                                },
                                            }}
                                        />
                                        <FormHelperText
                                            sx={{ display: 'flex', justifyContent: 'end' }}
                                        >
                                            {specialRequests?.length ?? 0}/
                                            {checkoutNotesField?.maxLimit || 100}
                                        </FormHelperText>
                                    </FormControl>
                                </Stack>
                            )}
                            <Stack
                                flexDirection={'row'}
                                sx={{ my: '20px' }}
                                justifyContent={'left'}
                            >
                                <Checkbox
                                    id={'terms_and_conditions_checkbox'}
                                    value="agreedWithTermsOfService"
                                    checked={agreedToToS}
                                    disabled={lineItems.length === 0}
                                    onChange={(e) => setAgreedtoToS(e.target.checked)}
                                    sx={{
                                        [`.MuiCheckbox-checkbox`]: {
                                            borderColor: organizerPrimaryColor,
                                        },
                                        [`.MuiCheckbox-checkbox.${checkboxClasses.disabled}`]: {
                                            borderColor: (theme) => theme.palette.neutral[400],
                                        },
                                        [`.MuiCheckbox-checkbox.${checkboxClasses.checked}`]: {
                                            backgroundColor: organizerPrimaryColor,
                                        },
                                    }}
                                />
                                <Typography sx={{ lineHeight: '143%', marginLeft: 1 }}>
                                    {t('checkoutCard.acceptTerms', "I accept the")}{' '}
                                    <a
                                        style={{
                                            color: organizerPrimaryColor,
                                        }}
                                        href={`${COMMUNITY_URL}community/terms-of-service`}
                                        target={'_blank'}
                                    >
                                        {t('checkoutCard.termsOfService', 'AdmitONE Terms of Service.')}
                                    </a>{' '}
                                    {t('checkoutCard.required', '(Required)')}
                                </Typography>
                            </Stack>
                            {hasAdditionalTerms && !hasQuestionnaire ? (
                                <>
                                    <StyledButton
                                        onClick={() => {
                                            setCart({
                                                checkoutNotes: specialRequests,
                                            });

                                            history.push('/additional-terms');
                                        }}
                                        disabled={!isEnabled}
                                        backgroundcolor={organizerPrimaryColor}
                                        id={'proceed_to_checkout'}
                                        size="lg"
                                    >
                                        {t('checkoutCard.proceed', 'Proceed')}
                                    </StyledButton>
                                </>
                            ) : hasAdditionalTerms && hasQuestionnaire ? (
                                <>
                                    <StyledButton
                                        onClick={() => {
                                            setCart({
                                                checkoutNotes: specialRequests,
                                            });

                                            history.push('/questionnaire');
                                        }}
                                        disabled={!isEnabled}
                                        backgroundcolor={organizerPrimaryColor}
                                        id={'proceed_to_checkout'}
                                        size="lg"
                                    >
                                        {t('checkoutCard.proceed', 'Proceed')}
                                    </StyledButton>
                                </>
                            ) : (
                                <>
                                    <CheckoutBtn
                                        history={history}
                                        backgroundcolor={organizerPrimaryColor}
                                        isAuthenticated={false}
                                        disabled={!isEnabled}
                                        btnText={
                                            soldOut
                                                ? t('checkoutCard.soldOut', 'Sold Out')
                                                : hasQuestionnaire
                                                ? t('checkoutCard.proceed', 'Proceed')
                                                : t('checkoutCard.proceedToCheckout', 'Proceed to Checkout')
                                        }
                                        checkoutNotes={specialRequests}
                                        chargeAmount={total}
                                        chargeCurrency={event!.currency}
                                        event={event}
                                        organizerPrimaryColor={organizerPrimaryColor}
                                        onContinueClick={onContinueClick}
                                        hasQuestionnaire={hasQuestionnaire}
                                    />
                                </>
                            )}
                        </>
                    )}
                </CheckoutStack>
                <div>
                    <img
                        src={event?.eventType === 'pro' ? ProLogo : Logo}
                        alt={t('checkoutCard.poweredBy', 'Powered by AdmitONE')}
                        style={{ width: '100%', maxWidth: '150px' }}
                    />
                </div>
            </Stack>
        </>
    );
}
